import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  Box,
  Grid,
  Snackbar,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { submitOrientationForm, getOrientationData } from '../Services/orientationService';
import LocationMap from '../Components/Map/LocationMap';
import YouTubePlayer from '../Components/Video/YouTubePlayer';
import PDFViewer from '../Components/PDF/PDFViewer';
import { useAuth } from '../contexts/JWTAuthContext';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(3, 0),
}));

const Orientation = () => {
  const { salesId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    feedback: '',
    IsInterested: true, // 'in' or 'out'
  });
  const [orientationData, setOrientationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchOrientationData = async () => {
      try {
        setLoading(true);
        const data = await getOrientationData(salesId);
        setOrientationData(data);
      } catch (error) {
        console.error('Error fetching orientation data:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load orientation data',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    if (salesId) {
      fetchOrientationData();
    }
  }, [salesId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const sessionId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('sessionId') || null;
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Submit form data
      await submitOrientationForm({
        ...formData,
        salesId,
        sessionId,
        userId: user?.id
      });
      // Send appropriate email based on status
      // if (formData.IsInterested === 'in') {
      //   await sendWelcomeEmail({
      //     ...formData,
      //     username: response.username,
      //     password: response.password,
      //   });
      // } else {
      //   await sendFeedbackEmail({
      //     ...formData,
      //   });
      // }

      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      const message =
        (typeof error === 'string' && error) ||
        error?.message ||
        'Failed to submit form';
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (submitted) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            Thank you for your feedback!
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <StyledPaper>
        <Typography variant="h4" component="h1" gutterBottom>
          Orientation Form
        </Typography>
        {/* <Typography variant="subtitle1" gutterBottom>
          Sales ID: {salesId}
        </Typography> */}

        {orientationData && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              {orientationData.videoId && (
                <YouTubePlayer videoId={orientationData.videoId} />
              )}
            </Grid>
            <Grid item xs={12}>
              {orientationData.pdfUrl && (
                <PDFViewer pdfUrl={orientationData.pdfUrl} />
              )}
            </Grid>
          </Grid>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                required
                fullWidth
                label="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                required
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                required
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Interested</FormLabel>
                <RadioGroup
                  row
                  name="IsInterested"
                  value={formData.IsInterested}
                  onChange={handleInputChange}
                >
                  <FormControlLabel value={true} control={<Radio />} label="In" disabled={loading} />
                  <FormControlLabel value={false} control={<Radio />} label="Out" disabled={loading} />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                required
                fullWidth
                multiline
                rows={4}
                label="Feedback"
                name="feedback"
                value={formData.feedback}
                onChange={handleInputChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </StyledPaper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Orientation; 